import { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Drawer,
  IconButton,
  Typography,
  Tooltip,
  Skeleton,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Search, X, Eye, BarChart2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useInstitutes } from '../../hooks/useInstitutes';
import { StatusBadge } from '../../components/StatusBadge';
import { InstituteDetailDrawer } from './InstituteDetailDrawer';
import type { InstituteListDto } from '../../types/institute';

type ActiveFilter = 'all' | 'active' | 'inactive';

export default function InstitutesPage() {
  const navigate = useNavigate();
  const { useList } = useInstitutes();

  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data, isLoading } = useList({
    pageNumber: page + 1,
    pageSize,
    search: search || undefined,
    isActive:
      activeFilter === 'all' ? undefined : activeFilter === 'active' ? true : false,
  });

  const rows = (data?.data ?? []).map((inst) => ({ ...inst, _id: inst.id }));

  const columns: GridColDef<InstituteListDto>[] = [
    {
      field: 'instituteName',
      headerName: 'Institute',
      flex: 1.5,
      minWidth: 180,
      renderCell: ({ row }) => (
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {row.instituteName}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
            {row.code}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: ({ row }) => (
        <StatusBadge status={row.isActive ? 'Active' : 'Inactive'} />
      ),
    },
    {
      field: 'onboardingStatus',
      headerName: 'Onboarding',
      width: 140,
      renderCell: ({ row }) => <StatusBadge status={row.onboardingStatus} />,
    },
    {
      field: 'onboardingProgressPercent',
      headerName: 'Progress',
      width: 130,
      renderCell: ({ row }) => (
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              flex: 1,
              height: 5,
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${row.onboardingProgressPercent}%`,
                borderRadius: 3,
                background: `linear-gradient(90deg, #8A70D6, #10B981)`,
                transition: 'width 300ms ease',
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0, width: 32, textAlign: 'right' }}>
            {row.onboardingProgressPercent}%
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }) => (
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {row.email ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'activatedAt',
      headerName: 'Activated',
      width: 120,
      renderCell: ({ row }) => (
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {row.activatedAt ? dayjs(row.activatedAt).format('DD MMM YYYY') : '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedId(row.id);
                setDrawerOpen(true);
              }}
            >
              <Eye size={15} />
            </IconButton>
          </Tooltip>
          <Tooltip title="View audit logs">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/audit-logs?instituteId=${row.id}`);
              }}
            >
              <BarChart2 size={15} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* Filters */}
      <Paper
        sx={{
          p: 1.5,
          mb: 2,
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          border: '1px solid rgba(255,255,255,0.06)',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          placeholder="Search institutes…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          size="small"
          sx={{ minWidth: 240 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={14} style={{ color: '#64748B' }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <X size={12} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
        <ToggleButtonGroup
          value={activeFilter}
          exclusive
          onChange={(_, v) => { if (v) { setActiveFilter(v); setPage(0); } }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: 600,
              border: '1px solid rgba(255,255,255,0.1)',
              '&.Mui-selected': {
                backgroundColor: 'rgba(138,112,214,0.15)',
                color: '#8A70D6',
                borderColor: 'rgba(138,112,214,0.3)',
              },
            },
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        {data && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {data.totalCount} institute{data.totalCount !== 1 ? 's' : ''}
          </Typography>
        )}
      </Paper>

      {/* Table */}
      <Paper sx={{ border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
        {isLoading ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={44} sx={{ borderRadius: '6px' }} />
            ))}
          </Box>
        ) : (
          <DataGrid
            rows={rows}
            getRowId={(r) => r.id}
            columns={columns}
            rowCount={data?.totalCount ?? 0}
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
            pageSizeOptions={[10, 25, 50]}
            onRowClick={(params) => {
              setSelectedId(params.row.id);
              setDrawerOpen(true);
            }}
            rowHeight={52}
            disableRowSelectionOnClick
            sx={{ border: 'none', minHeight: 400 }}
          />
        )}
      </Paper>

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedId(null); }}
        slotProps={{ paper: { sx: { width: 560 } } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.75,
            borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}
        >
          <Typography variant="h6">Institute Details</Typography>
          <IconButton size="small" onClick={() => { setDrawerOpen(false); setSelectedId(null); }}>
            <X size={16} />
          </IconButton>
        </Box>
        {selectedId && (
          <InstituteDetailDrawer
            id={selectedId}
            onClose={() => { setDrawerOpen(false); setSelectedId(null); }}
          />
        )}
      </Drawer>
    </Box>
  );
}
